/**
 * VIP VAPE — Collection Infinite Scroll (Horizon-style)
 * ---------------------------------------------------------------
 * - Uses Intersection Observer on a sentinel above the pagination
 * - Fetches next page as HTML and appends the product grid items only
 * - Updates browser URL via history.replaceState (no full reload)
 * - Respects "view all" / pagination fallback (works without JS)
 * - Stops at last page and shows "You've reached the end" message
 *
 * Activates only on .template-collection pages where:
 *   <div id="CollectionAjaxContent"> contains a <div data-collection-container>
 *   and a .pagination element with next page link.
 * ---------------------------------------------------------------
 */
(function () {
  'use strict';

  const SELECTORS = {
    container: '#CollectionAjaxContent',
    grid: '[data-collection-container]',
    pagination: '.pagination',
    nextLink: '.pagination .next a',
    productItem: '.grid__item',
  };

  const ROOT_MARGIN = '800px 0px';   // start fetching 800px before sentinel hits viewport
  const MAX_AUTO_LOADS = 8;          // safety cap; after this, show "Load more" button

  function init() {
    const container = document.querySelector(SELECTORS.container);
    if (!container) return;

    const grid = container.querySelector(SELECTORS.grid);
    const pagination = container.querySelector(SELECTORS.pagination);
    if (!grid) return;
    // No pagination present = single page collection, nothing to do
    if (!pagination) return;

    const initialNextLink = pagination.querySelector('.next a');
    if (!initialNextLink) return; // already on last page

    container.classList.add('vv-inf-active');

    let nextUrl = initialNextLink.getAttribute('href');
    let isLoading = false;
    let autoLoadCount = 0;
    let observer = null;
    let sentinel = null;
    let loader = null;

    function createSentinel() {
      sentinel = document.createElement('div');
      sentinel.className = 'vv-inf-sentinel';
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.style.cssText = 'width:100%;height:1px;';
      container.appendChild(sentinel);
    }

    function createLoader() {
      loader = document.createElement('div');
      loader.className = 'vv-inf-loader';
      loader.innerHTML = '<div class="vv-inf-loader__spinner"></div><div>Loading more products…</div>';
      loader.style.display = 'none';
      container.appendChild(loader);
    }

    function showLoader() { if (loader) loader.style.display = ''; }
    function hideLoader() { if (loader) loader.style.display = 'none'; }

    function showEndMessage() {
      const end = document.createElement('div');
      end.className = 'vv-inf-end';
      end.textContent = '— You\'ve reached the end —';
      container.appendChild(end);
    }

    function showLoadMoreButton() {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'btn btn--secondary vv-inf-load-more';
      btn.style.cssText = 'display:block;margin:24px auto;';
      btn.textContent = 'Load more products';
      btn.addEventListener('click', () => {
        btn.remove();
        autoLoadCount = 0;
        if (sentinel) observer.observe(sentinel);
        loadNext();
      });
      container.appendChild(btn);
    }

    async function loadNext() {
      if (isLoading || !nextUrl) return;
      isLoading = true;
      showLoader();

      try {
        const response = await fetch(nextUrl, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = await response.text();

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const newGrid = doc.querySelector(SELECTORS.grid);
        const newPagination = doc.querySelector(SELECTORS.pagination);

        if (!newGrid) throw new Error('No grid in next page');

        // Append each new product item with a subtle fade-in
        const newItems = newGrid.querySelectorAll(SELECTORS.productItem);
        const frag = document.createDocumentFragment();
        newItems.forEach((item) => {
          item.style.opacity = '0';
          item.style.transform = 'translateY(8px)';
          item.style.transition = 'opacity .35s ease, transform .35s ease';
          frag.appendChild(item);
        });
        grid.appendChild(frag);

        // Force reflow then animate in.
        //
        // ROOT CAUSE FIX (v2.8): Streamline's .image-element <img> has
        // CSS `opacity: 0` by default. It only becomes visible when AOS
        // adds the .aos-animate class to the parent [data-aos=image-fade-in]
        // wrapper, which then runs the `fade-in` keyframe `forwards`.
        //
        // AOS does NOT track DOM nodes inserted after init. Appended
        // product cards never get .aos-animate, so their .image-element
        // <img> stays opacity:0 forever — images appear blank.
        //
        // Fix: call AOS.refreshHard() (canonical Streamline pattern
        // used in theme.js after dynamic content). As a belt-and-braces
        // fallback for items already inside the viewport at append time,
        // also manually mark the aos wrapper as animated and unshim the
        // .image-wrap parent.
        requestAnimationFrame(() => {
          const viewportBottom = window.innerHeight + 400;
          newItems.forEach((item) => {
            item.style.opacity = '';
            item.style.transform = '';

            const rect = item.getBoundingClientRect();
            if (rect.top < viewportBottom) {
              // Force the fade-in animation by adding .aos-animate now
              item.querySelectorAll('[data-aos]').forEach((el) => {
                el.classList.add('aos-animate');
              });
              // Remove the .image-wrap shimmer overlay
              item.querySelectorAll('.image-wrap, .skrim__link').forEach((w) => {
                w.classList.add('loaded');
              });
            }
          });

          // Tell AOS to re-scan the DOM so newly-appended items below
          // the viewport bottom still animate in as the user scrolls.
          if (window.AOS && typeof window.AOS.refreshHard === 'function') {
            try { window.AOS.refreshHard(); } catch (e) {}
          }
        });

        // Update browser URL (no scroll, no reload)
        try { history.replaceState(null, '', nextUrl); } catch (e) {}

        // Notify other scripts (lazy images, product cards) that new content arrived
        document.dispatchEvent(new CustomEvent('vv:collection:appended', {
          detail: { count: newItems.length, url: nextUrl }
        }));

        // Update next URL for the next iteration
        const newNextLink = newPagination && newPagination.querySelector('.next a');
        nextUrl = newNextLink ? newNextLink.getAttribute('href') : null;

        autoLoadCount++;

        if (!nextUrl) {
          // Last page reached
          if (observer && sentinel) observer.unobserve(sentinel);
          if (sentinel) sentinel.remove();
          showEndMessage();
        } else if (autoLoadCount >= MAX_AUTO_LOADS) {
          // Pause auto-loading and show manual button so we don't trap user
          if (observer && sentinel) observer.unobserve(sentinel);
          showLoadMoreButton();
        }
      } catch (err) {
        console.error('[VVInfiniteScroll]', err);
        // On error: show retry button instead of dying silently
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'btn btn--secondary vv-inf-retry';
        retry.style.cssText = 'display:block;margin:16px auto;';
        retry.textContent = 'Couldn\'t load. Tap to retry';
        retry.addEventListener('click', () => { retry.remove(); loadNext(); });
        container.appendChild(retry);
      } finally {
        isLoading = false;
        hideLoader();
      }
    }

    createSentinel();
    createLoader();

    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) loadNext();
      });
    }, { rootMargin: ROOT_MARGIN, threshold: 0 });

    observer.observe(sentinel);

    // Public API for debugging / manual control
    window.VVInfiniteScroll = {
      loadNext,
      pause: () => { observer.unobserve(sentinel); },
      resume: () => { observer.observe(sentinel); }
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
